// Centralized GA4 analytics helper.
// Base gtag snippet is loaded in public/index.html; route-level page_view is sent
// by components/GAPageTracker.js. This module exposes typed helpers for the
// business events that compose the conversion funnel.
//
// Standard GA4 event names (sign_up, login, begin_checkout, purchase, etc.)
// are used when applicable so they auto-bind to GA4 conversions UI.

const GA_MEASUREMENT_ID = "G-C38SDSBS23";

const isGtagReady = () =>
  typeof window !== "undefined" && typeof window.gtag === "function";

/**
 * Generic safe wrapper - never throws if gtag isn't ready (ad-blocked, SSR, etc.).
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isGtagReady()) return;
  try {
    window.gtag("event", eventName, params);
  } catch (err) {
    // Silent: analytics must never break the app
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("[analytics] event failed:", eventName, err);
    }
  }
};

/** Identifies the current user in GA4 (hashed/internal ID only, no PII). */
export const setUserId = (userId) => {
  if (!isGtagReady() || !userId) return;
  try {
    window.gtag("config", GA_MEASUREMENT_ID, { user_id: String(userId) });
  } catch (_) {
    /* noop */
  }
};

// ------------------------------------------------------------------
// CONVERSION FUNNEL EVENTS
// ------------------------------------------------------------------

/** CTA click on landing / nav (e.g. "Analyser DCE", "S'inscrire"). */
export const trackCTAClick = (label, location = "unknown") =>
  trackEvent("cta_click", { cta_label: label, cta_location: location });

/** GA4 standard sign_up event (post-success). */
export const trackSignUp = (method = "email") =>
  trackEvent("sign_up", { method });

/** GA4 standard login event (post-success). */
export const trackLogin = (method = "email") =>
  trackEvent("login", { method });

/** User started a DCE analysis (file successfully sent to backend). */
export const trackAnalysisStart = ({ domain, documentType, mode }) =>
  trackEvent("analysis_start", {
    domain,
    document_type: documentType,
    mode,
  });

/** Analysis successfully returned. */
export const trackAnalysisCompleted = ({ domain, documentType, mode, durationMs }) =>
  trackEvent("analysis_completed", {
    domain,
    document_type: documentType,
    mode,
    duration_ms: durationMs,
  });

/** Analysis failed (network, timeout, server). */
export const trackAnalysisFailed = ({ domain, documentType, mode, reason }) =>
  trackEvent("analysis_failed", {
    domain,
    document_type: documentType,
    mode,
    reason,
  });

/** User downloaded the generated PDF synthesis. */
export const trackPdfDownload = ({ domain, documentType }) =>
  trackEvent("pdf_download", {
    domain,
    document_type: documentType,
  });

/** GA4 standard begin_checkout when user clicks subscribe / pay. */
export const trackBeginCheckout = ({ planId, value, currency = "EUR" }) =>
  trackEvent("begin_checkout", {
    currency,
    value,
    items: [
      {
        item_id: planId,
        item_name: planId,
        price: value,
        quantity: 1,
      },
    ],
  });

/** GA4 standard purchase event (post-Stripe success). */
export const trackPurchase = ({ transactionId, planId, value, currency = "EUR" }) =>
  trackEvent("purchase", {
    transaction_id: transactionId,
    value,
    currency,
    items: [
      {
        item_id: planId,
        item_name: planId,
        price: value,
        quantity: 1,
      },
    ],
  });
